import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PenLine, Loader2, Save, Upload, Stamp } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { aiApi } from "./aiApi";
import AISignaturePad from "./AISignaturePad";

const readFile = (f) => new Promise((ok) => { const r = new FileReader(); r.onload = () => ok(r.result); r.readAsDataURL(f); });
const Opt = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors ${on ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-fuchsia-300"}`}>{children}</button>
);

function SignatureBody({ s, setS, draft, setDraft }) {
  const stampMode = s.stamp ? "custom" : s.auto_stamp ? "auto" : "none";
  const pickStamp = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setS({ ...s, stamp: await readFile(f), auto_stamp: true }); };
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-[12.5px] font-semibold text-slate-700 cursor-pointer">
        <input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="accent-fuchsia-600" data-testid="ai-signature-enabled" />
        Letak tandatangan & cop automatik di hujung Surat Rasmi
      </label>
      <div><div className="text-[11px] font-bold text-slate-500 mb-1">Jawatan</div>
        <input value={s.jawatan} onChange={(e) => setS({ ...s, jawatan: e.target.value })} placeholder={s.default_jawatan}
          className="w-full h-9 rounded-xl border border-slate-200 px-2.5 text-[13px]" data-testid="ai-signature-jawatan" /></div>
      <div>
        <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-bold text-slate-500">Tandatangan</span>
          {s.signature && !draft && <button className="text-[11px] text-rose-600 font-semibold" onClick={() => setS({ ...s, signature: null, clear_signature: true })} data-testid="ai-signature-remove">Buang</button>}</div>
        {s.signature && !draft ? <div className="rounded-xl border border-slate-200 bg-white p-2 grid place-items-center"><img src={s.signature} alt="tandatangan" className="max-h-20" data-testid="ai-signature-current" /></div>
          : <AISignaturePad onChange={setDraft} />}
      </div>
      <div>
        <div className="text-[11px] font-bold text-slate-500 mb-1">Cop rasmi</div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          <Opt on={stampMode === "auto"} onClick={() => setS({ ...s, auto_stamp: true, stamp: null, clear_stamp: true })} testid="ai-stamp-auto">Cop automatik DJ</Opt>
          <label className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1 cursor-pointer ${stampMode === "custom" ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-200 text-slate-600"}`}>
            <Upload className="h-3 w-3" />Muat naik cop<input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pickStamp} data-testid="ai-stamp-upload" /></label>
          <Opt on={stampMode === "none"} onClick={() => setS({ ...s, auto_stamp: false, stamp: null, clear_stamp: true })} testid="ai-stamp-none">Tiada cop</Opt>
        </div>
        {stampMode !== "none" && <div className="grid place-items-center"><img src={s.stamp || s.auto_stamp_preview} alt="cop" className="h-24 w-24 object-contain" data-testid="ai-stamp-preview" /></div>}
      </div>
    </div>
  );
}

export default function AISignatureDialog({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDraft(null); aiApi.signature().then(setS).catch(() => toast.error("Gagal memuat tetapan tandatangan")); } }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { enabled: s.enabled, auto_stamp: s.auto_stamp, jawatan: s.jawatan, clear_signature: !!s.clear_signature && !draft, clear_stamp: !!s.clear_stamp && !s.stamp,
        ...(draft ? { signature: draft } : {}), ...(s.stamp?.startsWith("data:") && !s.stamp_saved ? { stamp: s.stamp } : {}) };
      await aiApi.saveSignature(body);
      toast.success("Tandatangan & cop disimpan"); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`h-7 px-2.5 rounded-full border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1 hover:border-fuchsia-300 hover:text-fuchsia-700 transition-colors ${className}`} data-testid="ai-signature-btn">
        <PenLine className="h-3 w-3" /> Tandatangan & cop
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-md z-[10006] max-h-[92vh] overflow-y-auto" data-testid="ai-signature-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Stamp className="h-5 w-5 text-fuchsia-600" /> Tandatangan Digital</DialogTitle></DialogHeader>
          {!s ? <div className="h-40 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <>
              <SignatureBody s={s} setS={setS} draft={draft} setDraft={setDraft} />
              <Button className="w-full rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={save} disabled={busy} data-testid="ai-signature-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Simpan</>}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
